import NoPhoto from "@/assets/images/Q_nophoto.jpeg";
import IconFacebook from "@/assets/images/icon-facebook.svg";
import IconInstagram from "@/assets/images/icon-instagram.svg";
import IconLinkedin from "@/assets/images/icon-linkedin.svg";
import IconTwitter from "@/assets/images/icon-twitter.svg";
import Layout from "@/components/elements/Layout";
import ArticleCardItem from "@/components/items/ArticleCardItem";
import OpinionCard from "@/components/items/OpinionCard";
import SpecialReportCardItem from "@/components/items/SpecialReportCardItem";
import SpotlightCardItem from "@/components/items/SpotlightCardItem";
import Schema from "@/components/schema/Schema";
import { getAuthorContents, getAuthorDetail } from "@/services/api/Author";
import Image from "next/legacy/image";
import Link from "next/link";

const AuthorDetail = ({ author, articles, spotlights, reports, opinions }) => {
  const structuredData = {
    "@context": "https://schema.org/",
    "@type": "Person",
    name: author.name,
    url: `https://lucidityinsights.com/author/${author.slug}`,
    image: author.photo_profile_url,
    sameAs: [author.instagram_url, author.linkedin_url, author.twitter_url],
    jobTitle: author.job_title,
    worksFor: {
      "@type": "Organization",
      name: "Lucidity Insights",
    },
  };
  return (
    <>
      <Schema data={structuredData} />
      <Layout
        title={author.name}
        description={
          "Lucidity Insights provides data-driven insights, stories and strategies for unlocking the potential of the MEAPT region's Venture Capital and Startup Ecosystems."
        }
      >
        <div className='max-w-6xl px-4 mx-auto 2xl:max-w-7xl'>
          <div className='flex flex-col items-center py-12 text-center text-white lg:items-start lg:py-16 lg:text-left lg:space-x-12 lg:flex-row'>
            <div className='flex-shrink-0 w-48 h-48 '>
              <Image
                src={author.photo_profile_url || NoPhoto}
                alt={author.photo_profile_alt || author.name}
                className='rounded-full'
                width={300}
                height={300}
                objectFit='cover'
              />
            </div>
            <div>
              <h1 className='mt-4 text-3xl font-bold lg:text-5xl lg:mt-0'>
                {author.name}
              </h1>
              <h2 className='mt-1 text-base font-medium text-transparent lg:text-lg bg-clip-text bg-gradient-to-tr from-yellow-1 to-red-1'>
                {author.job_title}
                {author.company_name && <span>, {author.company_name}</span>}
              </h2>
              <div
                className='mt-5'
                dangerouslySetInnerHTML={{
                  __html: author.description?.replace(/\r\n|\n/g, "<br />"),
                }}
              ></div>
              {author.linkedin_url && (
                <Link legacyBehavior passHref href={author.linkedin_url}>
                  <a className='inline-block w-8 h-8 mt-6' target={"_blank"}>
                    <Image src={IconLinkedin} alt='Lucidity Insights'></Image>
                  </a>
                </Link>
              )}
              {author.twitter_url && (
                <Link legacyBehavior passHref href={author.twitter_url}>
                  <a className='inline-block w-8 h-8 mt-6' target={"_blank"}>
                    <Image src={IconTwitter} alt='Lucidity Insights'></Image>
                  </a>
                </Link>
              )}
              {author.facebook_url && (
                <Link legacyBehavior passHref href={author.facebook_url}>
                  <a className='inline-block w-8 h-8 mt-6' target={"_blank"}>
                    <Image src={IconFacebook} alt='Lucidity Insights'></Image>
                  </a>
                </Link>
              )}
              {author.instagram_url && (
                <Link legacyBehavior passHref href={author.instagram_url}>
                  <a className='inline-block w-8 h-8 mt-6' target={"_blank"}>
                    <Image src={IconInstagram} alt='Lucidity Insights'></Image>
                  </a>
                </Link>
              )}
            </div>
          </div>
          {reports.length > 0 && (
            <div className='pb-16 text-white'>
              <h2 className='pb-3 text-xl font-semibold border-b border-white/10'>
                Latest Reports by {author.name}
              </h2>
              <div className='grid grid-cols-2 gap-3 mt-6 lg:grid-cols-4 xl:grid-cols-5'>
                {reports.map((item) => (
                  <div key={item.uuid}>
                    <SpecialReportCardItem {...item} />
                  </div>
                ))}
              </div>
            </div>
          )}
          {articles.length > 0 && (
            <div className='pb-16 text-white'>
              <h2 className='pb-3 text-xl font-semibold border-b border-white/10'>
                Latest Articles by {author.name}
              </h2>
              {/* <ArticlesSlider articles={contents} isLoading={article_loading} /> */}
              <div className='grid grid-cols-2 gap-3 mt-6 lg:grid-cols-4 xl:grid-cols-5'>
                {articles.map((item) => (
                  <div key={item.uuid}>
                    <ArticleCardItem {...item} />
                  </div>
                ))}
              </div>
            </div>
          )}
          {spotlights.length > 0 && (
            <div className='pb-16 text-white'>
              <h2 className='pb-3 text-xl font-semibold border-b border-white/10'>
                Latest Spotlights by {author.name}
              </h2>
              <div className='grid grid-cols-2 gap-3 mt-6 lg:grid-cols-4 xl:grid-cols-5'>
                {spotlights.map((item) => (
                  <div key={item.uuid}>
                    <SpotlightCardItem {...item} />
                  </div>
                ))}
              </div>
            </div>
          )}
          {opinions.length > 0 && (
            <div className='pb-16 text-white'>
              <h2 className='pb-3 text-xl font-semibold border-b border-white/10'>
                Latest Opinions by {author.name}
              </h2>
              <div className='grid grid-cols-2 gap-3 mt-6 lg:grid-cols-4 xl:grid-cols-5'>
                {opinions.map((item) => (
                  <div key={item.uuid}>
                    <OpinionCard data={{ ...item, author }} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Layout>
    </>
  );
};

export async function getServerSideProps({ params }) {
  const { slug } = params;
  const res = await getAuthorDetail(slug);
  const author = res.data;

  const reportRes = await getAuthorContents(author.id, {
    params: {
      contentTypeName: "Special Reports",
    },
  });
  const reports = reportRes.data;

  const articleRes = await getAuthorContents(author.id, {
    params: {
      contentTypeName: "Articles",
    },
  });
  const articles = articleRes.data;

  const spotlightRes = await getAuthorContents(author.id, {
    params: {
      contentTypeName: "Spotlights",
    },
  });
  const spotlights = spotlightRes.data;

  const opinionsRes = await getAuthorContents(author.id, {
    params: {
      contentTypeName: "Opinions",
    },
  });
  const opinions = opinionsRes.data;

  return {
    props: {
      author,
      articles,
      spotlights,
      reports,
      opinions,
    },
  };
}

export default AuthorDetail;
